/**
 * customerImportErrorsCsv
 *
 * Helper para gerar e baixar o CSV de erros de uma importação de CLIENTES.
 * Reusado pelo Step5Approval (dentro do wizard) e pelo menu de ações da
 * listagem em CustomerListsTab.
 *
 * RELEASE: 2026-05-17 v1.0.0 - Versão inicial (Fase 2)
 */

import { supabase } from '@/integrations/supabase/client';

interface ImportItem {
  row_index: number;
  status: string;
  raw_row: Record<string, unknown> | null;
  validation_errors: unknown;
  last_error: string | null;
}

function extractReason(validationErrors: unknown, lastError: string | null): string {
  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    return validationErrors
      .map((e: any) => {
        if (typeof e === 'string') return e;
        const field = e?.field ? `[${e.field}] ` : '';
        return `${field}${e?.message ?? JSON.stringify(e)}`;
      })
      .join(' • ');
  }
  if (validationErrors && typeof validationErrors === 'object') {
    return JSON.stringify(validationErrors);
  }
  if (typeof validationErrors === 'string') return validationErrors;
  return lastError ?? '—';
}

function escapeCsv(value: unknown): string {
  const s = value == null ? '' : String(value);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Busca todos os itens problemáticos (invalid/duplicate/blacklisted) do job
 * e dispara o download de um CSV com BOM contendo motivo + colunas originais.
 */
export async function downloadCustomerInvalidItemsCsv(
  jobId: string,
  jobName?: string | null,
): Promise<boolean> {
  const { data, error } = await (supabase as any)
    .from('customer_import_items')
    .select('row_index, status, raw_row, validation_errors, last_error')
    .eq('job_id', jobId)
    .in('status', ['invalid', 'duplicate', 'blacklisted'])
    .order('row_index', { ascending: true })
    .limit(50000);

  if (error) throw error;
  const items = (data ?? []) as ImportItem[];
  if (items.length === 0) return false;

  const cols = new Set<string>();
  items.forEach((it) => Object.keys(it.raw_row ?? {}).forEach((k) => cols.add(k)));
  const headers = ['linha', 'status', 'motivo', ...Array.from(cols)];

  const rows = items.map((it) => {
    const reason = extractReason(it.validation_errors, it.last_error);
    const base = [it.row_index, it.status, reason];
    const extras = Array.from(cols).map((c) => (it.raw_row as any)?.[c]);
    return [...base, ...extras].map(escapeCsv).join(';');
  });

  const csv = [headers.join(';'), ...rows].join('\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeName = (jobName ?? jobId).replace(/[^a-z0-9_-]+/gi, '_').slice(0, 40);
  a.download = `importacao-clientes-erros-${safeName}-${jobId.slice(0, 8)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  return true;
}
